import React, { useCallback, useEffect, useRef, useState } from 'react';

import API from 'files/api.js';
import AppConsent from 'files/AppConsent.js';
import AppNavigation from 'files/AppNavigation.js';
import Route from 'files/Route.js';
import Theme from 'styles/Theme.js';

/*
 * OmniShieldApp
 *
 * The homeowner's app, embedded. The frame is the whole view rather than a card
 * inside one: the app draws its own chrome and manages its own scrolling, so
 * anything wrapped around it would be a second set of margins fighting the
 * first.
 *
 * Sign in happens inside the frame. The dealer website has no accounts of its
 * own, so there is no identity here to hand over, and a login form on this page
 * would make every dealership site a place OmniShield passwords are typed.
 * Nothing about auth crosses this boundary.
 *
 * This site owns the address bar. The frame is told where to go and reports
 * where it went; it never writes to history itself. Two routers on one history
 * stack duplicate entries and make the back button skip. The bridge below is
 * the whole of that contract, and it is deliberately inert until the app ships
 * its side of the handshake - an unanswered `hello` costs nothing.
 */

// Until the app reports its height, the frame is a viewport tall. That is only
// ever what the visitor sees for the first paint - the app posts a  as
// soon as it has laid out, and every time its content changes after that.
const initial_height = 720;

// how long the host keeps introducing itself before giving up. the app answers
// within a frame or two of mounting; this only covers a slow first paint.
const hello_attempts = 20;
const hello_interval = 250;

const OmniShieldApp = ({ dealership, params }) => {

    const frame = useRef(null);
    const handshake = useRef(null);
    const ready = useRef(false);
    const reportViewport = useRef(null);
    const [height, setHeight] = useState(initial_height);
    const [loaded, setLoaded] = useState(false);
    
    const origin = API.dev_env ? 'http://localhost:8081' : 'https://app.homesafenetwork.com';

    const [source, setSource] = useState(null);

    useEffect(() => {
        if(source || !dealership?.id) {
            return;
        }
        let { params: routeParams } = Route.get();
        let query = new URLSearchParams();
        query.set('embedded', 'true');
        query.set('dealership', dealership.id);
        if(routeParams?.section) {
            query.set('section', routeParams.section);
        }
        if(routeParams?.n) {
            query.set('n', routeParams.n);
        }
        setSource(`${origin}/?${query.toString()}`);
    }, [dealership?.id, origin, source]);

    /*
     * The frame's src is fixed after the first render.
     *
     * Deep-link params are only an opening position: recomputing src when they
     * change would reload the whole app - losing its session, its scroll and any
     * open layer - every time someone used the header. Once it is up, the app is
     * told where to go with a message instead, which is why the effect below
     * keeps whatever it resolved the first time.
     */

    const onPost = useCallback(message => {
        if(!frame.current?.contentWindow) {
            return;
        }
        frame.current.contentWindow.postMessage({
            source: 'omnishield',
            v: 1,
            ...message
        }, origin);
    }, [origin]);

    /*
     * The introduction, repeated until it is answered.
     *
     * The app learns which origin to address its replies to from the first
     * message we send it, so this is not a courtesy - a hello that arrives
     * before its listener is mounted leaves the app unable to answer at all,
     * and the frame never resizes. Any message back proves it is listening,
     * which is why the retry stops on the first one rather than on a
     * particular type.
     */
    const onLoad = () => {

        setLoaded(true);

        let attempts = 0;
        const greet = () => {

            if(ready.current === true || attempts >= hello_attempts) {
                window.clearInterval(handshake.current);
                handshake.current = null;
                return;
            }
            attempts++;
            onPost({ type: 'hello', payload: { dealerId: null, locale: 'en', chrome: 'app', v: 1 } });
        };

        window.clearInterval(handshake.current);
        handshake.current = window.setInterval(greet, hello_interval);
        greet();
    }

    // an app that never answers must not leave the retry running behind it
    useEffect(() => () => window.clearInterval(handshake.current), []);

    /*
     * Which slice of the frame the visitor can actually see.
     *
     * The frame is as tall as the app, so inside it there is no viewport - a
     * layer or an alert has nothing to position against and would open at the
     * top of a very long document. This is the only side that can measure it.
     */
    useEffect(() => {

        let pending = null;

        const report = () => {
            pending = null;
            if(!frame.current) {
                return;
            }
            let rect = frame.current.getBoundingClientRect();

            // the site header is fixed, so the top of the window is not the top
            // of what the visitor can see - the first headerHeight pixels are
            // behind it. a layer measured against the window opens underneath
            // the header and loses its heading off the top edge.
            let inset = Theme.layout.headerHeight();
            let top = Math.max(0, Math.min(rect.height, inset - rect.top));
            let bottom = Math.max(0, Math.min(rect.height, window.innerHeight - rect.top));
            onPost({
                type: 'viewport',
                payload: {
                    height: Math.max(0, bottom - top),
                    offsetTop: top
                }
            });
        };

        // one report per frame - scrolling fires far faster than the app can use
        const schedule = () => {
            if(pending !== null) {
                return;
            }
            pending = window.requestAnimationFrame(report);
        };

        // the app asks for this when it mounts something that has to position
        // itself, which is the only reliable moment - scroll and resize may
        // never fire before then
        reportViewport.current = schedule;

        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        schedule();

        return () => {
            if(pending !== null) {
                window.cancelAnimationFrame(pending);
            }
            reportViewport.current = null;
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
        };

    }, [height, loaded, onPost]);

    // navigation from this site's own header, once the app is listening
    useEffect(() => {
        if(!params?.section) {
            return;
        }
        onPost({
            type: 'navigate',
            payload: {
                n: params.n,
                section: params.section
            }
        });
    }, [params?.section, params?.n, loaded, onPost]);

    // sign out asked for from this site's header
    useEffect(() => {
        return AppNavigation.subscribeToSignOut(() => {
            onPost({ type: 'signOut' });
        });
    }, [onPost]);

    useEffect(() => {

        // the root modal reports which key was pressed, or null if it was
        // closed without answering. this is the only component holding the
        // channel, so it does the posting.
        return AppConsent.subscribeToAnswer(key => {
            if(key === null) {
                onPost({ type: 'consentDismissed' });
                return;
            }
            onPost({
                payload: { analytics: key === 'accept' },
                type: 'consentResponse'
            });
        });
    }, [onPost]);

    useEffect(() => {

        const onMessage = event => {

            // origin first, before anything in the payload is read. dev tools,
            // extensions and third party scripts all post into this window.
            if(event.origin !== origin || event.data?.source !== 'omnishield') {
                return;
            }

            // anything at all means the app is listening and knows where to
            // reply, so the introduction can stop
            ready.current = true;
            if(handshake.current !== null) {
                window.clearInterval(handshake.current);
                handshake.current = null;
            }

            switch(event.data.type) {
                case 'ready':
                break;

                case 'navigationChanged':
                // the app deciding what its own menu contains. the header reads
                // this rather than keeping a copy of the section list.
                AppNavigation.set(event.data.payload);
                break;

                case 'signOutComplete':
                // the app has finished tearing its session down, so it is safe
                // to leave. leaving the visitor on a signed-out frame would just
                // show them the sign in screen with no way back into the site.
                AppNavigation.set({ authenticated: false, sections: [] });
                Route.push('home');
                break;

                case 'openPrivacyPolicy':
                // the app cannot open the policy itself without spawning a
                // second copy of this site in a new tab. it owns the address
                // bar here, so it navigates.
                Route.push('privacy');
                break;

                case 'consentRequest':
                /*
                 * The app has a privacy question to put and has handed it over
                 * to be asked in this site's own chrome. It is published rather
                 * than rendered here: consent belongs to the visit, not to the
                 * page hosting the frame, so the root draws it. This component
                 * only does the part it is uniquely able to do - talk to the
                 * frame - and never reads into the payload.
                 */
                AppConsent.set(event.data.payload || null);
                break;

                case 'consentDismiss':
                AppConsent.set(null);
                break;

                case 'scrollLock':
                // an overlay is open inside the frame. holding this page still
                // stops the overlay chasing a moving target, and the visitor is
                // not trying to read what is behind it. the padding replaces the
                // scrollbar's width so the page does not jump sideways.
                if(event.data.payload?.locked === true) {
                    let gap = window.innerWidth - document.documentElement.clientWidth;
                    document.body.style.overflow = 'hidden';
                    document.body.style.paddingRight = gap > 0 ? `${gap}px` : '';
                } else {
                    document.body.style.overflow = '';
                    document.body.style.paddingRight = '';
                }
                break;

                case 'requestViewport':
                if(typeof(reportViewport.current) === 'function') {
                    reportViewport.current();
                }
                break;

                case 'resize':
                // the app grows to its own content and does not scroll, so this
                // page is the only scroller. nothing out here can measure inside
                // a cross-origin document, which is why it has to be told.
                if(typeof(event.data.payload?.height) === 'number') {
                    setHeight(Math.max(0, Math.ceil(event.data.payload.height)));
                    if(typeof(reportViewport.current) === 'function') {
                        reportViewport.current();
                    }
                }
                break;

                case 'stateChanged':
                // the frame reporting where it is, mirrored into this site's url
                // so a refresh and a share both land back in the same place
                Route.replace('app', {
                    n: event.data.payload?.commLinkGuid,
                    section: event.data.payload?.section
                });
                break;

                default:
                break;
            }
        };

        window.addEventListener('message', onMessage);
        return () => {
            window.removeEventListener('message', onMessage);

            // navigating away with an overlay open would otherwise leave the
            // whole site unscrollable
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [origin]);

    return (
        <div style={{
            backgroundColor: window.theme === 'dark' ? 'rgba(25,25,25,1)' : '#F0F1F5',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            width: '100%'
        }}>
            {(loaded === false || !source) && (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    height: height,
                    justifyContent: 'center',
                    left: 0,
                    position: 'absolute',
                    right: 0,
                    top: 0
                }}>
                    <div style={{
                        animation: 'omnishield-frame-spin 0.7s linear infinite',
                        border: `2px solid ${Theme.colors.border()}`,
                        borderRadius: '50%',
                        borderTopColor: Theme.colors.primary(),
                        height: 28,
                        width: 28
                    }} />
                    <style>{'@keyframes omnishield-frame-spin { to { transform: rotate(360deg); } }'}</style>
                </div>
            )}
            {source && (
                <iframe
                allow={'clipboard-write; geolocation'}
                onLoad={onLoad}
                ref={frame}
                src={source}
                style={{
                    border: 'none',
                    display: 'block',
                    height: height,
                    opacity: loaded ? 1 : 0,
                    transition: 'opacity 0.25s ease',
                    width: '100%'
                }}
                title={'OmniShield'} />
            )}
        </div>
    )
}

export default OmniShieldApp;
